'use client'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { Link as AscendLink } from '@achieve/ascend'
import { _get } from 'utils/shared'
import {
  TypographyStringType,
  createRenderNodeDefault,
  handleContentToMapper,
  optionsDefault,
  selectOverride,
} from 'components/Contentful/Typography/Typography'
import { BorderTextBox } from 'components/BorderTextBox'
import { LinkButton, MediaImageStandard, SuperScript } from 'components/Contentful'
import { VideoDialog } from 'components/VideoDialog'
import { VideoPlayer } from 'components/VideoPlayer'
import { rewriteAssetUrl } from 'utils/conversions'
import { UncrawlableLinkClient } from 'components/Contentful/Typography'
import { removeSpecialCharacters } from 'utils/shared'
import { ChildComponentGeneratorClient } from 'components/ContentHandlersAppRouter/ChildComponentGeneratorClient'
import dynamic from 'next/dynamic'
import { NO_CRAWL_ID_PLACEHOLDER_REGEX } from 'constants/regex'
import { sanitizeCmsHtml } from 'utils/sanitize/sanitize'
import { SITE_ENVIRONMENTS } from 'constants/websites'

const DTICalculator = dynamic(() =>
  import(/* webpackChunkName: "DTICalculator" */ 'pageSections/DTICalculator/DTICalculator', {
    ssr: false,
  })
)
const PopUpSection = dynamic(() =>
  import(/* webpackChunkName: "PopUpSection" */ 'pageSections/PopUpSection/PopUpSection', {
    ssr: false,
  })
)

const Calculator = dynamic(() =>
  import(/* webpackChunkName: "HelocCalculatorSection" */ 'components/Calculator', { ssr: false })
)

const CardAppCTA = dynamic(() =>
  import(/* webpackChunkName: "CardAppCTA" */ 'components/Cards/CardAppCTA/CardAppCTA', {
    ssr: false,
  })
)

const CardBottomCTA = dynamic(() =>
  import(/* webpackChunkName: "CardBottomCTA" */ 'components/Cards/CardBottomCTA/CardBottomCTA', {
    ssr: false,
  })
)

const CardCTA = dynamic(() =>
  import(/* webpackChunkName: "CardCTA" */ 'components/Cards/CardCTA/CardCTA', { ssr: false })
)

const TrustPilotStateWidget = dynamic(() =>
  import(
    /* webpackChunkName: "TrustPilotStateWidget" */ 'pageSections/TrustPilotStateWidget/TrustPilotStateWidget',
    { ssr: false }
  )
)

const ExpandedContent = dynamic(
  () =>
    import(
      /* webpackChunkName: "ExpandedContent" */ 'pageSections/ExpandedContent/ExpandedContent'
    ).then((mod) => ({ default: mod.KeyTakeawaysContent })),
  { ssr: false }
)

import { AchieveLink } from 'components/AchieveLink'
import { ClientSideTypographyClient } from 'components/Contentful/Typography'
import { DataStripContent } from 'components/DataStripContent/DataStripContent'
import { EditorsNote } from 'components/EditorsNote/EditorsNote'
import { QuickTipContent } from 'components/QuickTipContent/QuickTipContent'
import { ProsAndConsContent } from 'components/ProsAndConsContent/ProsAndConsContent'
import { MarkdownTableContent } from 'components/MarkdownTableContent/MarkdownTableContent'
import { ProductProviderCards } from 'components/ProductProviderCards/ProductProviderCards'
import ButtonCTAv2 from 'components/ButtonCTAv2/index'
import ButtonCTA from 'components/ButtonCTA/index'
import { AdvertisingDisclosures } from 'pageSections/HeroDebt/components/AdvertisingDisclosures'

const RICH_TEXT = {
  [BLOCKS.PARAGRAPH]: { variant: 'labelMd', component: 'p' },
  [BLOCKS.HEADING_1]: { variant: 'headingLg', component: 'h1' },
  [BLOCKS.HEADING_2]: {
    variant: 'headingMd',
    component: 'h2',
    fontWeight: 'medium',
  },
  [BLOCKS.HEADING_3]: { variant: 'bodyLg', component: 'h3', fontWeight: 'medium' },
  [BLOCKS.HEADING_4]: { variant: 'labelMd', component: 'h4', fontWeight: 'medium' },
  [BLOCKS.HEADING_5]: { variant: 'bodyXs', component: 'h5' },
  [BLOCKS.HEADING_6]: { variant: 'bodyXs', component: 'h6' },
  [BLOCKS.OL_LIST]: { component: 'ol' },
  [BLOCKS.UL_LIST]: { component: 'ul' },
  [BLOCKS.LIST_ITEM]: { component: 'li' }, //sx properties apply to single child element
  [BLOCKS.HR]: {},
  [BLOCKS.EMBEDDED_ASSET]: {},
  [BLOCKS.TABLE]: { variant: 'bodyLg', component: 'table' },
  [BLOCKS.TABLE_ROW]: { variant: 'bodyLg', component: 'tr' },
  [BLOCKS.TABLE_CELL]: { variant: 'bodyLg', component: 'td' },
  [BLOCKS.TABLE_HEADER_CELL]: { variant: 'bodyLg', component: 'th' },
  [INLINES.HYPERLINK]: { component: 'a' }, // inlines should inherit the font variant
  [INLINES.ASSET_HYPERLINK]: { component: 'a' }, // inlines should inherit the font variant
  // Currently unimplemented in Ascend
  //[MARKS.CODE]: {component: 'code'},
  //[BLOCKS.QUOTE]:{},
}

const embeddedPageSection = (node, contentType) => {
  const pageSection = _get(node, 'data.target')
  return (
    <ChildComponentGeneratorClient
      contentTypes={{ [pageSection?.fields?.uiComponent]: contentType }}
      uiComponent={pageSection?.fields?.uiComponent}
      {...pageSection}
      key={pageSection?.sys?.id}
    />
  )
}

/**
 * Render Contentful TypographyLongForm components from Contentful Rich Text
 * To build the variantOverride object you will need to import import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
 * @param {Object} content
 * @param {Object} variantOverride object of rich-text-types as keys and Ascend Variants as the value
 * @param {Object} styleOverride object of rich-text-types as keys and a MUI "sx" object as the value
 * @returns
 */
function TypographyLongForm({
  content: typographyContent = '',
  variantOverride,
  styleOverride,
  ...props
}) {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || SITE_ENVIRONMENTS.DEFAULT
  const isWfsSite = siteEnv === SITE_ENVIRONMENTS.WFS
  const embeddedLinkButtonClassName = isWfsSite
    ? 'bg-[#cb000e] hover:bg-[#7A0D0D] hover:text-[white] leading-[1.2]'
    : ''

  if (typeof typographyContent === 'string') {
    const stringTypeProps = {
      ...props,
      className:
        'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
    }
    return TypographyStringType({
      styleOverride,
      variantOverride,
      typographyContent,
      props: stringTypeProps,
      richText: RICH_TEXT,
    })
  }
  // otherwise must be an object
  const { textContent } = handleContentToMapper(typographyContent)

  const createRenderNode = (
    node,
    children,
    blockType,
    className = 'typography-default',
    variantToChild = false,
    styleToChild = false,
    extraProps = {}
  ) => {
    // defaults data test id and id
    const fallbackTestId = _get(node, 'content[0].value')
      ? `${_get(node, 'content[0].value')}`.slice(0, 50)
      : 'no-id-needed'

    const fallbackId = _get(props, 'id')
      ? _get(props, 'id')
      : `${BLOCKS[blockType]}-${(_get(node, 'content[0].value') ?? '')
          .slice(0, 50)
          .replaceAll(' ', '-')}`

    // use extraProps when provided, otherwise fall back to defaults
    const dataTestId = extraProps['data-testid'] ?? fallbackTestId
    const id = extraProps.id ?? fallbackId

    const propsComponent = {
      ['data-testid']: dataTestId,
      fontWeight: RICH_TEXT[BLOCKS[blockType]].fontWeight,
      id: removeSpecialCharacters(id),
      'no-crawl-id': extraProps['no-crawl-id'] ?? undefined,
      ...props,
    }

    return createRenderNodeDefault({
      children,
      blockType,
      variantToChild,
      styleToChild,
      variantOverride,
      styleOverride,
      className,
      richText: RICH_TEXT,
      propsComponent,
    })
  }

  const optionsInit = optionsDefault({
    createRenderNode,
    props,
    styleOverride,
    variantOverride,
    richText: RICH_TEXT,
  })

  const options = {
    renderMark: { ...optionsInit.renderMark },
    renderNode: {
      ...optionsInit.renderNode,
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const onlyOneText =
          Array.isArray(node.content) &&
          node.content.length === 1 &&
          node.content[0]?.nodeType === 'text' &&
          typeof node.content[0]?.value === 'string'

        if (onlyOneText) {
          const val = node.content[0].value
          const m = val.match(NO_CRAWL_ID_PLACEHOLDER_REGEX)
          if (m) {
            return createRenderNode(
              node,
              [], // no children => empty <p>
              'PARAGRAPH',
              'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
              false,
              false,
              {
                ['data-testid']: m[0],
                id: m[0],
                ['no-crawl-id']: m[0],
              }
            )
          }
        }

        return createRenderNode(
          node,
          children,
          'PARAGRAPH',
          'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]'
        )
      },
      [BLOCKS.HEADING_1]: (node, children) =>
        createRenderNode(node, children, 'HEADING_1', 'mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_2]: (node, children) =>
        createRenderNode(node, children, 'HEADING_2', 'mt-10 mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_3]: (node, children) =>
        createRenderNode(node, children, 'HEADING_3', 'mt-10 mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_4]: (node, children) =>
        createRenderNode(
          node,
          children,
          'HEADING_4',
          'mt-10 mb-[20px] scroll-mt-[72px] leading-[170%]!',
          true
        ),
      [BLOCKS.HEADING_5]: (node, children) =>
        createRenderNode(node, children, 'HEADING_5', 'mb-10 scroll-mt-[72px]', true),
      [BLOCKS.HEADING_6]: (node, children) =>
        createRenderNode(node, children, 'HEADING_6', 'mb-10 scroll-mt-[72px]', true),
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const linkId = node?.content[0]?.value
        const url = rewriteAssetUrl(node?.data?.target?.fields?.file.url || node?.data.url)
        return (
          <AscendLink
            target="_blank"
            sx={selectOverride(INLINES.ASSET_HYPERLINK, styleOverride, 'sx', RICH_TEXT)}
            variant={selectOverride(INLINES.ASSET_HYPERLINK, variantOverride, 'variant', RICH_TEXT)}
            data-testid={`Link-${url}-${linkId}`}
            href={url}
            aria-label={`${children} - link opens in a new tab`}
            {...props}
            component={RICH_TEXT[INLINES.ASSET_HYPERLINK].component}
            className={'p-0 scroll-mt-[72px]'}
          >
            {children}
          </AscendLink>
        )
      },
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        // TODO: Handle other types of inline entries. Currently,
        //       node is a data object with no content. Rendering in
        //       an html tag does not work, invalid React child
        const {
          plainText,
          styledText,
          variation,
          identifier,
          videoPlayer,
          uiComponent,
          fieldName,
        } = _get(node, 'data.target.fields', {})
        const { contentType } = _get(node, 'data.target.sys', {})
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'Pop-Up') {
          return embeddedPageSection(node, PopUpSection)
        }
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'DTICalculator') {
          return embeddedPageSection(node, DTICalculator)
        }
        if (contentType?.sys?.id === 'calculator') {
          const { fields } = _get(node, 'data.target', {})
          return <Calculator content={fields} />
        }
        if (contentType?.sys?.id === 'articleCtaCard') {
          const { fields } = _get(node, 'data.target', {})
          return <CardCTA content={fields} trackingName={'In-Content'} />
        }
        if (fieldName === 'advertisingDisclosures') {
          return <AdvertisingDisclosures />
        }
        if (uiComponent === 'CardAppCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardAppCTA content={fields} />
        }
        if (uiComponent === 'CardBottomCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardBottomCTA content={fields} />
        }
        if (uiComponent === 'LinkButton') {
          const { target } = _get(node, 'data', {})
          return (
            <span className={'inline-flex my-2'}>
              <LinkButton content={target} className={embeddedLinkButtonClassName} />
            </span>
          )
        }

        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <span data-scrolto={identifier}></span>
        }
        if (styledText === 'scrollFrom') {
          return (
            <AscendLink
              sx={selectOverride(INLINES.ASSET_HYPERLINK, styleOverride, 'sx', RICH_TEXT)}
              variant={selectOverride(
                INLINES.ASSET_HYPERLINK,
                variantOverride,
                'variant',
                RICH_TEXT
              )}
              data-testid={`Scroll-to-${plainText}`}
              onClick={() => {
                const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                if (el) {
                  // Adding -70px to fix element padding
                  window.scrollTo({
                    top: el.offsetTop - 72,
                    behavior: 'smooth',
                  })
                }
              }}
              aria-label={`Scroll to ${plainText}`}
              {...props}
              component={RICH_TEXT[INLINES.ASSET_HYPERLINK].component}
            >
              {plainText}
            </AscendLink>
          )
        }
        if (videoPlayer) {
          const { target } = _get(node, 'data', {})
          return <VideoDialog content={target} />
        }
        if (uiComponent === 'VideoPlayer') {
          const target = _get(node, 'data.target.fields', {})
          return <VideoPlayer content={target} frameClassName={'w-full h-auto'} frameEmbed={true} />
        }
        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkClient
              node={node}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
            />
          )
        }
        if (uiComponent === 'Typography') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <TypographyLongForm
              {...props}
              component="span"
              content={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <ClientSideTypographyClient
              textContent={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (node?.data?.uiComponent === 'TypographyClientSide') {
          const data = _get(node, 'data.textContent', {})
          return (
            <ClientSideTypographyClient
              textContent={data}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        return null
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // TODO: Handle other types of inline entries. Currently,
        //       node is a data object with no content. Rendering in
        //       an html tag does not work, invalid React child
        const {
          plainText,
          styledText,
          variation,
          identifier,
          videoPlayer,
          uiComponent,
          fieldName,
          textContent,
          name,
          variant, // Only being used in textFooterRemarkable type. TODO: Spike to see if we can implement this with a new entry for single line rich text without using Typography component
        } = _get(node, 'data.target.fields', {})
        const { contentType } = _get(node, 'data.target.sys', {})
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'Pop-Up') {
          return embeddedPageSection(node, PopUpSection)
        }
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'DTICalculator') {
          return embeddedPageSection(node, DTICalculator)
        }

        if (contentType?.sys?.id === 'calculator') {
          const { fields } = _get(node, 'data.target', {})
          return <Calculator content={fields} />
        }

        if (fieldName === 'advertisingDisclosures') {
          return <AdvertisingDisclosures />
        }

        if (fieldName == 'editorsNote') {
          const { fields } = _get(node, 'data.target', {})
          return <EditorsNote {...fields} />
        }

        if (fieldName == 'quickTip') {
          return <QuickTipContent content={textContent} name={name} />
        }
        if (contentType?.sys?.id === 'articleCtaCard') {
          const { fields } = _get(node, 'data.target', {})
          return <CardCTA content={fields} trackingName={'In-Content'} />
        }
        if (uiComponent === 'CardAppCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardAppCTA content={fields} />
        }
        if (uiComponent === 'CardBottomCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardBottomCTA content={fields} />
        }
        if (uiComponent === 'LinkButton') {
          const { target } = _get(node, 'data', {})
          return (
            <div className={'my-6'}>
              <LinkButton content={target} className={embeddedLinkButtonClassName} />
            </div>
          )
        }
        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <span data-scrolto={identifier}></span>
        }
        if (uiComponent === 'MediaField') {
          const imageWidth = _get(
            node,
            'data.target.fields.mediaContent.fields.file.details.image.width'
          )
          const imageHeight = _get(
            node,
            'data.target.fields.mediaContent.fields.file.details.image.height'
          )
          const imageContent = _get(node, 'data.target.fields.mediaContent')
          const href = _get(node, 'data.target.fields.href')
          return (
            <span className={'items-center flex justify-center'}>
              {href ? (
                <>
                  <AchieveLink
                    href={href}
                    track={{
                      click_id: `MediaField-TypograpyLongForm`,
                      event_action: 'link_click',
                      event_type: 'link_click',
                      click_url: href,
                    }}
                    data-testid={'media-link'}
                  >
                    <MediaImageStandard
                      content={imageContent}
                      width={imageWidth}
                      height={imageHeight}
                      className={'object-contain h-full w-full'}
                      {...props}
                    />
                  </AchieveLink>
                </>
              ) : (
                <MediaImageStandard
                  content={imageContent}
                  width={imageWidth}
                  height={imageHeight}
                  className={'object-contain h-full w-full'}
                  {...props}
                />
              )}
            </span>
          )
        }
        if (styledText === 'scrollFrom') {
          return (
            <AscendLink
              sx={selectOverride(INLINES.ASSET_HYPERLINK, styleOverride, 'sx', RICH_TEXT)}
              variant={selectOverride(
                INLINES.ASSET_HYPERLINK,
                variantOverride,
                'variant',
                RICH_TEXT
              )}
              data-testid={`Scroll-to-${plainText}`}
              onClick={() => {
                const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                if (el) {
                  // Adding -70px to fix element padding
                  window.scrollTo({
                    top: el.offsetTop - 72,
                    behavior: 'smooth',
                  })
                }
              }}
              aria-label={`Scroll to ${plainText}`}
              {...props}
              component={RICH_TEXT[INLINES.ASSET_HYPERLINK].component}
            >
              {plainText}
            </AscendLink>
          )
        }
        if (videoPlayer) {
          const { target } = _get(node, 'data', {})
          return <VideoDialog content={target} />
        }
        if (uiComponent === 'VideoPlayer') {
          const target = _get(node, 'data.target.fields', {})
          return (
            <VideoPlayer
              content={target}
              frameClassName={'w-full h-auto'}
              frameEmbed={true}
              aspectRatio={16 / 9}
            />
          )
        }
        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkClient
              node={node}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
            />
          )
        }
        if (uiComponent === 'Typography') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <TypographyLongForm
              {...props}
              content={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <ClientSideTypographyClient
              textContent={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (node?.data?.uiComponent === 'TypographyClientSide') {
          const data = _get(node, 'data.textContent', {})
          return (
            <ClientSideTypographyClient
              textContent={data}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (fieldName == 'textFooterRemarkable') {
          return <BorderTextBox content={textContent} variant={variant} id={name} />
        }
        if (contentType?.sys?.id === 'dataStrip') {
          const { target } = _get(node, 'data', {})
          return <DataStripContent {...target} />
        }
        if (contentType?.sys?.id === 'iframe') {
          const { content } = _get(node, 'data.target.fields', {})
          return (
            <div className="chart" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(content) }} />
          )
        }
        if (contentType?.sys?.id === 'prosAndCons') {
          const { fields } = _get(node, 'data.target', {})
          return <ProsAndConsContent content={fields} />
        }
        if (contentType?.sys?.id === 'table') {
          const { fields } = _get(node, 'data.target', {})
          return <MarkdownTableContent content={fields} />
        }
        if (contentType?.sys?.id === 'productProviderCards') {
          const { fields } = _get(node, 'data.target', {})
          return <ProductProviderCards content={fields} />
        }
        if (uiComponent === 'TrustPilotStateWidget') {
          const { fields } = _get(node, 'data.target', {})
          return <TrustPilotStateWidget content={fields} />
        }
        if (uiComponent === 'ExpandedContent') {
          const { fields } = _get(node, 'data.target', {})
          return <ExpandedContent content={fields} />
        }
        return null
      },
      sliderCTA: ({ data }) => {
        const fields = {
          name: 'CTA 0 - Bills Article',
          title: {
            data: {},
            content: [
              {
                data: {},
                content: [
                  {
                    data: {},
                    marks: [],
                    value: data?.title,
                    nodeType: 'text',
                  },
                ],
                nodeType: 'heading-3',
              },
            ],
            nodeType: 'document',
          },
          subtitle: {
            data: {},
            content: [
              {
                data: {},
                content: [
                  {
                    data: {},
                    marks: [],
                    value: 'Or speak to a debt consultant ',
                    nodeType: 'text',
                  },
                  {
                    data: { uri: `tel:${data?.phone}` },
                    content: [{ data: {}, marks: [], value: data?.phone, nodeType: 'text' }],
                    nodeType: 'hyperlink',
                  },
                ],
                nodeType: 'paragraph',
              },
            ],
            nodeType: 'document',
          },
          link: {
            metadata: { tags: [], concepts: [] },
            sys: {
              space: { sys: { type: 'Link', linkType: 'Space', id: '80jeq2aeq4l1' } },
              id: '17qhOD8ZVhACmel3B48HYh',
              type: 'Entry',
              createdAt: '2025-09-25T03:55:02.662Z',
              updatedAt: '2025-09-25T03:58:13.128Z',
              environment: { sys: { id: 'dev', type: 'Link', linkType: 'Environment' } },
              publishedVersion: 6,
              revision: 2,
              contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'lInk' } },
              locale: 'en-US',
            },
            fields: {
              name: 'Get Started - Bills Link',
              uiComponent: 'LinkButton',
              fieldName: 'billsArticleCtaLink',
              linkText: {
                data: {},
                content: [
                  {
                    data: {},
                    content: [{ data: {}, marks: [], value: data?.btnLabel, nodeType: 'text' }],
                    nodeType: 'paragraph',
                  },
                ],
                nodeType: 'document',
              },
              linkHref: data?.url,
            },
          },
        }
        return <CardCTA content={fields} trackingName={'In-Content'} />
      },
      buttonCTAv2: ({ data }) => {
        return (
          <ButtonCTAv2
            title={data?.title}
            items={data?.items}
            url={data?.url}
            buttonText={data?.buttonText}
            underButtonText={data?.underButtonText}
          />
        )
      },
      buttonCTA: ({ data }) => {
        return (
          <ButtonCTA
            description={data?.description}
            href={data?.url}
            label={data?.label}
            location={data?.location}
          />
        )
      },
    },
    renderText: optionsInit.renderText,
  }

  return documentToReactComponents(textContent, options)
}

export default TypographyLongForm
export { TypographyLongForm, RICH_TEXT }
